import { apiClient } from './client';
import { API_ENDPOINTS } from './config';

export interface WorkspaceResponse {
  id: string;
  name: string;
  ownerUserId: string;
  avatarUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkspaceRequest {
  name: string;
}

export interface UpdateWorkspaceRequest {
  name?: string;
}

export interface WorkspaceMemberResponse {
  id: string;
  workspaceId: string;
  userId: string;
  role: 'member' | 'admin' | 'owner';
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
}

export interface WorkspaceInvitationResponse {
  id: string;
  workspaceId: string;
  email: string;
  role: 'member' | 'admin';
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  invitedByUserId: string;
  invitedBy?: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateInvitationRequest {
  email: string;
  role?: 'member' | 'admin';
}

export interface WorkspaceIntegrationResponse {
  id: string;
  workspaceId: string;
  provider: string;
  config: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export const workspacesApi = {
  async getAll(): Promise<WorkspaceResponse[]> {
    return apiClient.get<WorkspaceResponse[]>(API_ENDPOINTS.WORKSPACES.BASE);
  },

  async getById(id: string): Promise<WorkspaceResponse> {
    return apiClient.get<WorkspaceResponse>(API_ENDPOINTS.WORKSPACES.BY_ID(id));
  },

  async create(data: CreateWorkspaceRequest): Promise<WorkspaceResponse> {
    return apiClient.post<WorkspaceResponse>(API_ENDPOINTS.WORKSPACES.BASE, data);
  },

  async update(id: string, data: UpdateWorkspaceRequest): Promise<WorkspaceResponse> {
    return apiClient.patch<WorkspaceResponse>(API_ENDPOINTS.WORKSPACES.BY_ID(id), data);
  },

  async uploadAvatar(id: string, file: File): Promise<WorkspaceResponse> {
    const form = new FormData();
    form.append('file', file);
    return apiClient.postForm<WorkspaceResponse>(
      `${API_ENDPOINTS.WORKSPACES.BY_ID(id)}/avatar`,
      form
    );
  },

  async delete(id: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.WORKSPACES.BY_ID(id));
  },

  // Members
  async getMembers(workspaceId: string): Promise<WorkspaceMemberResponse[]> {
    return apiClient.get<WorkspaceMemberResponse[]>(API_ENDPOINTS.WORKSPACES.MEMBERS(workspaceId));
  },

  async removeMember(workspaceId: string, memberId: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.WORKSPACES.MEMBER_BY_ID(workspaceId, memberId));
  },

  // Invitations
  async getInvitations(workspaceId: string): Promise<WorkspaceInvitationResponse[]> {
    return apiClient.get<WorkspaceInvitationResponse[]>(API_ENDPOINTS.WORKSPACES.INVITATIONS(workspaceId));
  },

  async createInvitation(workspaceId: string, data: CreateInvitationRequest): Promise<WorkspaceInvitationResponse> {
    return apiClient.post<WorkspaceInvitationResponse>(API_ENDPOINTS.WORKSPACES.INVITATIONS(workspaceId), data);
  },

  async removeInvitation(workspaceId: string, invitationId: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.WORKSPACES.INVITATION_BY_ID(workspaceId, invitationId));
  },

  // Integrations
  async upsertIntegration(workspaceId: string, provider: string, config: any): Promise<WorkspaceIntegrationResponse> {
    return apiClient.post<WorkspaceIntegrationResponse>(
      `${API_ENDPOINTS.WORKSPACES.BY_ID(workspaceId)}/integrations/${provider}`,
      { config }
    );
  },

  async getIntegration(workspaceId: string, provider: string): Promise<WorkspaceIntegrationResponse> {
    return apiClient.get<WorkspaceIntegrationResponse>(
      `${API_ENDPOINTS.WORKSPACES.BY_ID(workspaceId)}/integrations/${provider}`
    );
  },
};

